import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  Avatar,
  Fab,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Checkbox,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { teamsAPI, agentsAPI } from '../services/api';

interface Team {
  id: number;
  name: string;
  description: string | null;
  allow_auto_assign: boolean;
  created_at: string;
  updated_at: string;
}

interface TeamMember {
  id: number;
  user_id: number;
  team_id: number;
  is_admin: boolean;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url?: string;
    role: string;
    availability_status: string;
  };
}

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  role: string;
  availability_status: string;
}

interface TeamFormData {
  name: string;
  description: string;
  allow_auto_assign: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TeamsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [teams, setTeams] = useState<Team[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ [teamId: number]: TeamMember[] }>({});
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAutoAssign, setFilterAutoAssign] = useState<boolean | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);

  const [formData, setFormData] = useState<TeamFormData>({
    name: '',
    description: '',
    allow_auto_assign: true
  });

  useEffect(() => {
    fetchTeams();
    fetchAgents();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const data = await teamsAPI.getTeams();
      setTeams(data);
      // Fetch members for each team
      data.forEach((team: Team) => fetchTeamMembers(team.id));
    } catch (error) {
      console.error('Error fetching teams:', error);
      setSnackbar({ open: true, message: 'Failed to fetch teams', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await agentsAPI.getAgents();
      setAgents(data.agents || data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchTeamMembers = async (teamId: number) => {
    try {
      const data = await teamsAPI.getTeamMembers(teamId);
      setTeamMembers(prev => ({ ...prev, [teamId]: data }));
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const formatErrorMessage = (error: any): string => {
    if (error.response?.data) {
      // Check if it's a validation error (array of error objects)
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail
          .map((err: any) => err.msg || JSON.stringify(err))
          .join(', ');
      } else if (typeof error.response.data.detail === 'string') {
        return error.response.data.detail;
      } else if (typeof error.response.data.detail === 'object') {
        // If it's still an object, extract the message
        return error.response.data.detail.msg ||
               error.response.data.detail.message ||
               JSON.stringify(error.response.data.detail);
      }
    }
    return 'An error occurred';
  };

  const handleSubmit = async () => {
    try {
      if (editingTeam) {
        await teamsAPI.updateTeam(editingTeam.id, formData);
        setSnackbar({
          open: true,
          message: 'Team updated successfully',
          severity: 'success'
        });
      } else {
        await teamsAPI.createTeam(formData);
        setSnackbar({
          open: true,
          message: 'Team created successfully',
          severity: 'success'
        });
      }
      fetchTeams();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving team:', error);
      const errorMessage = formatErrorMessage(error) || 'Failed to save team';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDelete = async (team: Team) => {
    try {
      await teamsAPI.deleteTeam(team.id);
      setSnackbar({ open: true, message: 'Team deleted successfully', severity: 'success' });
      fetchTeams();
    } catch (error: any) {
      console.error('Error deleting team:', error);
      const errorMessage = formatErrorMessage(error) || 'Failed to delete team';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setTeamToDelete(null);
    }
  };

  const handleAddAgentsToTeam = async () => {
    if (!selectedTeam) return;

    try {
      const currentMembers = teamMembers[selectedTeam.id] || [];
      const currentMemberIds = currentMembers
        .filter(member => member.user)
        .map(member => member.user.id);
      const selectedAgentIds = selectedAgents.map(agent => agent.id);

      // Find agents to add (in selected but not in current)
      const agentsToAdd = selectedAgentIds.filter(id => !currentMemberIds.includes(id));

      // Find agents to remove (in current but not in selected)
      const agentsToRemove = currentMemberIds.filter(id => !selectedAgentIds.includes(id));

      // Add new members
      for (const agentId of agentsToAdd) {
        await teamsAPI.addTeamMember(selectedTeam.id, agentId, false);
      }

      // Remove members
      for (const agentId of agentsToRemove) {
        await teamsAPI.removeTeamMember(selectedTeam.id, agentId);
      }

      setSnackbar({ open: true, message: 'Team members updated successfully', severity: 'success' });
      fetchTeamMembers(selectedTeam.id);
      setOpenMembersDialog(false);
      setSelectedAgents([]);
    } catch (error: any) {
      console.error('Error updating team members:', error);
      const errorMessage = formatErrorMessage(error) || 'Failed to update team members';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleRemoveAgentFromTeam = async (teamId: number, agentId: number) => {
    try {
      await teamsAPI.removeTeamMember(teamId, agentId);
      setSnackbar({ open: true, message: 'Agent removed from team', severity: 'success' });
      fetchTeamMembers(teamId);
    } catch (error: any) {
      console.error('Error removing agent:', error);
      const errorMessage = formatErrorMessage(error) || 'Failed to remove agent';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleOpenDialog = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        description: team.description || '',
        allow_auto_assign: team.allow_auto_assign
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        description: '',
        allow_auto_assign: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
  };

  const handleOpenMembersDialog = (team: Team) => {
    setSelectedTeam(team);
    const currentMembers = teamMembers[team.id] || [];
    const currentMemberIds = currentMembers
      .filter(member => member.user)
      .map(member => member.user.id);
    const currentSelectedAgents = agents.filter(agent => currentMemberIds.includes(agent.id));
    setSelectedAgents(currentSelectedAgents);
    setOpenMembersDialog(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, team: Team) => {
    setAnchorEl(event.currentTarget);
    setSelectedTeam(team);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTeam(null);
  };

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterAutoAssign === null || team.allow_auto_assign === filterAutoAssign;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: teams.length,
    autoAssign: teams.filter(t => t.allow_auto_assign).length,
    totalMembers: Object.values(teamMembers).reduce((sum, members) => sum + members.length, 0)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return '#22c55e';
      case 'busy': return '#f59e0b';
      case 'offline': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const renderTeamCard = (team: Team) => {
    const members = teamMembers[team.id] || [];
    
    return (
      <Card key={team.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{ 
                  bgcolor: theme.palette.primary.main, 
                  width: 32, 
                  height: 32
                }}
              >
                <GroupIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {team.name}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleMenuClick(e, team)}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          
          {team.description && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {team.description}
            </Typography>
          )}

          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Members:
              </Typography>
              <Chip
                size="small"
                label={members.length}
                variant="outlined"
              />
            </Box>

            {team.allow_auto_assign && (
              <Chip
                size="small"
                label="Auto-assignment enabled"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {members.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Team Members:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {members.slice(0, 3).map((member) => (
                  member.user ? (
                    <Chip
                      key={member.id}
                      size="small"
                      avatar={
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: getStatusColor(member.user?.availability_status || 'offline')
                          }}
                        >
                          {member.user.first_name?.[0] || '?'}
                        </Avatar>
                      }
                      label={`${member.user.first_name} ${member.user.last_name}`}
                      variant="outlined"
                    />
                  ) : null
                ))}
                {members.length > 3 && (
                  <Chip
                    size="small"
                    label={`+${members.length - 3} more`}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}
        </CardContent>
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<PersonIcon />}
            onClick={() => handleOpenMembersDialog(team)}
          >
            Manage Members
          </Button>
        </Box>
      </Card>
    );
  };

  const renderTableView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Team</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Members</TableCell>
            <TableCell>Auto-assign</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTeams.map((team) => {
            const members = teamMembers[team.id] || [];
            return (
              <TableRow key={team.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{ 
                        bgcolor: theme.palette.primary.main, 
                        width: 24, 
                        height: 24
                      }}
                    >
                      <GroupIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {team.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {team.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={members.length}
                      variant="outlined"
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleOpenMembersDialog(team)}
                    >
                      Manage
                    </Button>
                  </Box>
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={team.allow_auto_assign}
                    disabled
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, team)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Teams
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Team
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Teams
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Auto-assign Teams
              </Typography>
              <Typography variant="h4" component="div">
                {stats.autoAssign}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Members
              </Typography>
              <Typography variant="h4" component="div">
                {stats.totalMembers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="Filter by auto-assign"
                value={filterAutoAssign === null ? 'all' : filterAutoAssign ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterAutoAssign(value === 'all' ? null : value === 'true');
                }}
              >
                <MenuItem value="all">All Teams</MenuItem>
                <MenuItem value="true">Auto-assign Only</MenuItem>
                <MenuItem value="false">Manual Only</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1}>
                <IconButton
                  onClick={() => setViewMode('cards')}
                  color={viewMode === 'cards' ? 'primary' : 'default'}
                >
                  <ViewModuleIcon />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode('table')}
                  color={viewMode === 'table' ? 'primary' : 'default'}
                >
                  <ViewListIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Teams Content */}
      {filteredTeams.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No teams found' : 'No teams yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchTerm 
                ? 'Try adjusting your search or filters'
                : 'Create your first team to organize your agents'
              }
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Team
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <Grid container spacing={2}>
              {filteredTeams.map((team) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={team.id}>
                  {renderTeamCard(team)}
                </Grid>
              ))}
            </Grid>
          ) : (
            renderTableView()
          )}
        </>
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Team Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editingTeam ? 'Edit Team' : 'Add New Team'}
            {isMobile && (
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allow_auto_assign}
                    onChange={(e) => setFormData({ ...formData, allow_auto_assign: e.target.checked })}
                  />
                }
                label="Allow auto-assignment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingTeam ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog 
        open={openMembersDialog} 
        onClose={() => setOpenMembersDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Manage Team Members - {selectedTeam?.name}
            {isMobile && (
              <IconButton edge="end" color="inherit" onClick={() => setOpenMembersDialog(false)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Current Members" />
                <Tab label="Add Members" />
              </Tabs>
            </Box>
            
            <TabPanel value={tabValue} index={0}>
              <List>
                {selectedTeam && teamMembers[selectedTeam.id]?.map((member) => (
                  member.user ? (
                    <ListItem key={member.id}>
                      <ListItemAvatar>
                        <Avatar
                          src={member.user?.avatar_url}
                          sx={{ bgcolor: getStatusColor(member.user?.availability_status || 'offline') }}
                        >
                          {member.user.first_name?.[0] || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${member.user.first_name} ${member.user.last_name}`}
                        secondary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                              {member.user.email}
                            </Typography>
                            {member.is_admin && <AdminIcon fontSize="small" color="primary" />}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => selectedTeam && handleRemoveAgentFromTeam(selectedTeam.id, member.user.id)}
                        >
                          <PersonRemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ) : null
                ))}
                {selectedTeam && (!teamMembers[selectedTeam.id] || teamMembers[selectedTeam.id].length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No members in this team
                  </Typography>
                )}
              </List>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Autocomplete
                multiple
                options={agents}
                value={selectedAgents}
                onChange={(event, newValue) => setSelectedAgents(newValue)}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select agents to add"
                    placeholder="Search agents..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar 
                      sx={{ 
                        mr: 2, 
                        width: 32, 
                        height: 32,
                        bgcolor: getStatusColor(option.availability_status)
                      }}
                      src={option.avatar_url}
                    >
                      {option.first_name[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {option.first_name} {option.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </TabPanel>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenMembersDialog(false)}>Cancel</Button>
          {tabValue === 1 && (
            <Button 
              onClick={handleAddAgentsToTeam} 
              variant="contained"
              disabled={selectedAgents.length === 0}
            >
              Update Members
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{teamToDelete?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => teamToDelete && handleDelete(teamToDelete)} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedTeam) handleOpenDialog(selectedTeam);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedTeam) handleOpenMembersDialog(selectedTeam);
          handleMenuClose();
        }}>
          <PersonIcon sx={{ mr: 1 }} fontSize="small" />
          Manage Members
        </MenuItem>
        <MenuItem onClick={() => {
          setTeamToDelete(selectedTeam);
          setDeleteConfirmOpen(true);
          handleMenuClose();
        }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamsPage;